/**
 * Design document attachments section — upload to OBS, metadata to BSP.
 * Supports multi-file upload and download links.
 */

import { useEffect, useState } from 'react';
import { Button, Typography, Space, Popconfirm, message, Upload, Spin, Alert } from 'antd';
import {
  UploadOutlined,
  DeleteOutlined,
  PaperClipOutlined,
} from '@ant-design/icons';
import {
  listDesignDocumentAttachments,
  createDesignDocumentAttachment,
  deleteDesignDocumentAttachment,
} from '@/api/requirements';
import { getUploadUrl, getDownloadUrl, proxyDownloadFile } from '@/api/storage';
import type { DesignDocumentAttachment } from '@/types';
import { useAuthStore } from '@/store/authStore';
import {
  calculateFileMD5,
  validateUploadResponse,
  verifyUploadedFile,
  cleanupFailedUpload,
} from '@/utils/uploadUtils';
import { uploadLog, uploadError } from '@/utils/uploadLogger';

const { Text, Link } = Typography;

const formatFileSize = (bytes: number) => {
  if (!bytes || isNaN(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

interface DesignDocumentAttachmentsProps {
  projectId: number;
  documentId: number;
  onAttachmentsChange?: () => void;
}

const DesignDocumentAttachments: React.FC<DesignDocumentAttachmentsProps> = ({
  projectId,
  documentId,
  onAttachmentsChange,
}) => {
  const [attachments, setAttachments] = useState<DesignDocumentAttachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const { user } = useAuthStore();
  const isInternal = user?.user_type === 'internal';

  const fetchAttachments = async () => {
    setLoading(true);
    try {
      const data = await listDesignDocumentAttachments(projectId, documentId);
      setAttachments(data);
    } catch {
      message.error('加载附件失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttachments();
  }, [projectId, documentId]);

  const uploadOneFile = async (file: File): Promise<DesignDocumentAttachment> => {
    // 1. Calculate MD5 hash for file integrity verification
    const md5 = await calculateFileMD5(file);
    uploadLog(`[Upload:MD5] ${file.name}: ${md5}`);

    // 2. Request signed upload URL from backend
    const { url, object_key } = await getUploadUrl(
      file.name,
      file.type || 'application/octet-stream'
    );
    uploadLog(`[Upload:SignedUrl] ${file.name}: object_key=${object_key}`);

    // 3. Upload file directly to OBS with MD5 validation
    const response = await fetch(url, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
      },
    });

    // 4. Validate upload response (status code, ETag, MD5 match)
    await validateUploadResponse(response, md5, object_key, file.name);
    uploadLog(`[Upload:Validated] ${file.name}: ETag matches MD5`);

    // 5. Verify file exists in OBS (backend verification)
    try {
      await verifyUploadedFile(object_key, file.size, file.name);
    } catch (verifyError) {
      uploadError(`[Upload:VerificationFailed] ${file.name}:`, verifyError);
      // Cleanup failed upload from OBS
      await cleanupFailedUpload(object_key, file.name);
      throw new Error(
        `File verification failed after upload: ${
          verifyError instanceof Error ? verifyError.message : 'Unknown error'
        }`
      );
    }

    // 6. All validations passed - create database record
    uploadLog(`[Upload:CreateDB] ${file.name}: Creating attachment record`);
    return createDesignDocumentAttachment(projectId, documentId, {
      file_name: file.name,
      file_url: object_key,
      object_key,
      file_size: file.size,
    });
  };

  const handleUpload = async (fileList: File[]) => {
    if (fileList.length === 0) return;
    setUploading(true);
    const results: DesignDocumentAttachment[] = [];
    let failCount = 0;

    for (const file of fileList) {
      try {
        const att = await uploadOneFile(file);
        results.push(att);
      } catch {
        failCount++;
      }
    }

    if (results.length > 0) {
      setAttachments([...results.reverse(), ...attachments]);
      message.success(`${results.length} 个附件上传成功`);
      onAttachmentsChange?.();
    }
    if (failCount > 0) {
      message.error(`${failCount} 个附件上传失败`);
    }
    setUploading(false);
  };

  const handleDownload = async (att: DesignDocumentAttachment) => {
    if (user?.user_type === 'external') {
      proxyDownloadFile(att.file_url, att.file_name);
    } else {
      try {
        const { url } = await getDownloadUrl(att.file_url);
        window.open(url, '_blank');
      } catch {
        message.error('获取下载链接失败');
      }
    }
  };

  const handleDelete = async (attachmentId: number) => {
    try {
      await deleteDesignDocumentAttachment(projectId, documentId, attachmentId);
      setAttachments(attachments.filter((a) => a.id !== attachmentId));
      message.success('附件已删除');
      onAttachmentsChange?.();
    } catch {
      message.error('删除附件失败');
    }
  };

  return (
    <div>
      {/* Attachment info alert */}
      <Alert
        title="附件说明"
        description="在此上传的附件将立即归属于当前最新版本。如需为新版本上传附件，请使用'发布新版本'功能。"
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      {/* Upload button - only for internal users */}
      {isInternal && (
        <Upload
          multiple
          beforeUpload={(file, fileList) => {
            if (file === fileList[0]) {
              handleUpload(fileList as unknown as File[]);
            }
            return false;
          }}
          showUploadList={false}
        >
          <Button
            icon={<UploadOutlined />}
            loading={uploading}
            size="small"
            style={{ marginBottom: 12 }}
          >
            上传附件
          </Button>
        </Upload>
      )}

      {loading ? (
        <Spin size="small" />
      ) : attachments.length === 0 ? (
        <Text type="secondary">暂无附件</Text>
      ) : (
        <div>
          {attachments.map((item) => (
            <div
              key={item.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 0',
                borderBottom: '1px solid #f5f5f5',
              }}
            >
              <PaperClipOutlined style={{ color: '#999', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Link
                    style={{ fontSize: 13 }}
                    onClick={() => handleDownload(item)}
                    ellipsis
                  >
                    {item.file_name}
                  </Link>
                  {item.file_size > 0 && (
                    <Text type="secondary" style={{ fontSize: 12, flexShrink: 0 }}>
                      {formatFileSize(item.file_size)}
                    </Text>
                  )}
                </div>
                <Space size={8} style={{ fontSize: 12 }}>
                  <Text type="secondary">
                    {item.created_by?.display_name || item.created_by?.username}
                  </Text>
                  <Text type="secondary">
                    {new Date(item.created_at).toLocaleString('zh-CN')}
                  </Text>
                </Space>
              </div>
              {/* Delete button - only for internal users */}
              {isInternal && (
                <Popconfirm title="确认删除？" onConfirm={() => handleDelete(item.id)}>
                  <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DesignDocumentAttachments;
